import React from "react";
import { motion } from "framer-motion";

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-bodyMain py-12 px-6 md:px-20">
      <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg p-10">
        {/* Page Heading */}
        <motion.h1
          className="text-4xl font-extrabold text-purple-700 text-center mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          PawDash Terms of Service
        </motion.h1>

        <p className="text-gray-500 text-center mb-8">
          Last Updated: <strong>June 23, 2025</strong>
        </p>

        {/* Section */}
        <motion.section
          className="space-y-8 text-gray-700"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          {/* 1. Introduction */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">1. Introduction</h2>
            <p>
              Pawdash provides pet delivery and walking services in the United Arab Emirates (UAE). These Terms apply to all users of our Services, including customers, partners, and third parties.
            </p>
          </div>

          {/* 2. Acceptance of Terms */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">2. Acceptance of Terms</h2>
            <p>
              By accessing or using Pawdash’s Services, you affirm that you are at least 18 years old, have the legal capacity to enter into these Terms, and agree to be bound by them.
            </p>
          </div>

          {/* 3. Description of Services */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">3. Description of Services</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Pet Delivery Services:</strong> Transporting pets to various destinations within the UAE.
              </li>
              <li>
                <strong>Pet Walking Services:</strong> Professional walking services for pets.
              </li>
              <li>
                <strong>Additional Services:</strong> Including but not limited to pet grooming, boarding, and training, as available.
              </li>
            </ul>
          </div>

          {/* 4. User Responsibilities */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">4. User Responsibilities</h2>
            <p>
              You must be the pet owner or authorized to arrange services. You agree to provide accurate pet information and ensure proper identification and health/vaccine status.
            </p>
          </div>

          {/* 5. Service Limitations and Risks */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">5. Service Limitations and Risks</h2>
            <p>
              You acknowledge the inherent risks in pet transport and handling. Pawdash is not liable for injury or illness unless due to gross negligence or intentional misconduct.
            </p>
          </div>

          {/* 6. Payment Terms */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">6. Payment Terms</h2>
            <p>
              Service fees are as per the latest pricing and payable via accepted methods. Refunds are subject to our refund policy.
            </p>
          </div>

          {/* 7. Data and Privacy */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">7. Data and Privacy</h2>
            <p>
              We collect and use data as per our Privacy Policy. You may review, update, or delete your information anytime.
            </p>
          </div>

          {/* 8. Advertising and Third Parties */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">8. Advertising and Third Parties</h2>
            <p>
              We may include advertising in the service and share data with partners solely for operational needs.
            </p>
          </div>

          {/* 9. Consent for Home Access */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">9. Consent for Home Access</h2>
            <p>
              By requesting services that require access to your property, you consent to PawDash employees entering for pet-related service execution. PawDash shall not be held liable for lost or damaged property unless caused by intentional misconduct. You are responsible for removing or securing valuables beforehand.
            </p>
          </div>

          {/* 10. Emergency and Veterinary Care */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">10. Emergency and Veterinary Care</h2>
            <p>
              You authorize PawDash to seek emergency care if necessary during services. You will bear all costs associated with treatment and care.
            </p>
          </div>

          {/* 11. Limitation of Liability */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">11. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Pawdash shall not be liable for any indirect, incidental, special, or consequential damages.
            </p>
          </div>

          {/* 12. Indemnification */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Pawdash, its employees, and partners from any claims arising out of your use of services or your pet’s actions.
            </p>
          </div>

          {/* 13. Refund and Cancellation Policy */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">13. Refund and Cancellation Policy</h2>
            <p>
              PawDash operates a no-refund and no-cancellation policy for all booked services. However, in exceptional cases, a refund or cancellation request may be considered at the sole discretion of PawDash management. Such requests must be submitted in writing and will be evaluated based on the circumstances.
            </p>
          </div>

          {/* 14. Governing Law */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">14. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the United Arab Emirates.
            </p>
          </div>

          {/* 15. Contact Information */}
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-3">15. Contact Information</h2>
            <ul className="list-disc pl-6">
              <li><strong>Email:</strong> info@mypawdash.com</li>
              <li><strong>Address:</strong> Abu Dhabi, UAE</li>
              <li><strong>Phone:</strong> +971 50 992 1927</li>
            </ul>
          </div>
        </motion.section>

        {/* Back to Home Button */}
        <div className="mt-8 flex justify-center">
          <motion.a
            href="/"
            className="px-6 py-3 bg-purple-700 text-white text-lg rounded-lg shadow-lg hover:bg-purple-800 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Home
          </motion.a>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
